import type { CSSProperties } from 'react'

/*
 * The one look for the kit's round buttons (WorldButton, HoldButton, CornerButton): a domed face
 * with a soft highlight, a bevelled edge, and a ledge underneath, so every button reads as a thing
 * to press. Selected lifts and rings; pressed sinks onto its ledge. Any face colour works: the
 * light and shade are white and ink overlays on top of it.
 */

export type ButtonSkinState = 'rest' | 'selected' | 'pressed'

const INK = '16, 32, 48'

export function buttonSkin({ face = '#ffffff', state, ring, ledge = 5, stretch, ringGap }: {
  face?: string
  state: ButtonSkinState
  /** The selection (or held) ring colour; pressed shows it too when given. */
  ring?: string
  /** Ledge depth in px (how far the button sinks when pressed). */
  ledge?: number
  /**
   * A round button stretched into a pill (`ScrubButton` growing into its adjuster; border radius
   * half the height): the dome's highlight keeps the round button's size at the round end and
   * widens along the pill, so it still reads as one domed face. Round when width = height.
   */
  stretch?: { width: number; height: number }
  /** The ring as a thin line a little off the face (white gap, then `ring`): the "tidier ring" selected look. */
  ringGap?: boolean
}): CSSProperties {
  // Highlight ellipse and centre: the round button's percentages, or px for a stretched one.
  const at = (x: number, y: number) => {
    if (!stretch) return `120% 120% at ${x}% ${y}%`
    const { width: w, height: h } = stretch
    const extra = Math.max(0, w - h)
    return `${1.2 * h + 0.6 * extra}px 120% at ${(x / 100) * h + 0.15 * extra}px ${y}%`
  }
  const dome = state === 'pressed'
    ? `radial-gradient(${at(34, 26)}, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.15) 40%, rgba(255,255,255,0) 62%)`
    : `radial-gradient(${at(32, 22)}, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.4) 36%, rgba(255,255,255,0) 60%)`
  const shade = `linear-gradient(180deg, rgba(${INK},0) 45%, rgba(${INK},${state === 'pressed' ? 0.04 : 0.1}) 100%)`
  const rim = `0 0 0 1px rgba(${INK},0.1)`
  const ringShadow = ring ? (ringGap ? `0 0 0 2.5px #ffffff, 0 0 0 5.5px ${ring}` : `0 0 0 5px ${ring}`) : rim
  const shadow =
    state === 'pressed'
      ? [
          `inset 0 3px 6px rgba(${INK},0.22)`,
          'inset 0 -1px 0 rgba(255,255,255,0.7)',
          ring ? ringShadow : rim,
          `0 1px 0 rgba(${INK},0.26)`,
          `0 2px 5px rgba(${INK},0.16)`,
        ]
      : state === 'selected'
        ? [
            'inset 0 2px 0 rgba(255,255,255,0.95)',
            `inset 0 -4px 6px rgba(${INK},0.12)`,
            ringShadow,
            `0 ${ledge + 6}px 18px rgba(${INK},0.26)`,
          ]
        : [
            'inset 0 2px 0 rgba(255,255,255,0.95)',
            `inset 0 -4px 6px rgba(${INK},0.14)`,
            rim,
            `0 ${ledge}px 0 rgba(${INK},0.24)`,
            `0 ${ledge + 5}px 14px rgba(${INK},0.18)`,
          ]
  return {
    background: `${dome}, ${shade}, ${face}`,
    boxShadow: shadow.join(', '),
    transform:
      state === 'pressed' ? `translateY(${ledge - 1}px) scale(0.97)`
        : state === 'selected' ? 'translateY(-5px) scale(1.05)'
          : 'translateY(0) scale(1)',
    transition: state === 'pressed'
      ? 'transform 70ms ease-out, box-shadow 70ms ease-out'
      : 'transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 200ms ease-out',
  }
}
